import React, { useState } from 'react'
import { Target, Zap, ChevronDown, ChevronRight } from 'lucide-react'
import { formatCompact, formatPercent, formatPrice, formatSignedPercent } from '../utils/format'
import { toneColor } from '../utils/tone'

const BULL = 'var(--onds-bull)'
const BEAR = 'var(--onds-bear)'

const pct = (value, max) => (max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0)

const tiltLabel = (ratio) => {
  if (!ratio) return { text: 'no data', tone: 'neutral' }
  if (ratio < 0.7) return { text: 'call-heavy · bullish tilt', tone: 'bull' }
  if (ratio > 1.3) return { text: 'put-heavy · bearish tilt', tone: 'bear' }
  return { text: 'balanced', tone: 'caution' }
}

const mean = (values) => (values.length ? values.reduce((sum, v) => sum + v, 0) / values.length : 0)

const summarizeHorizon = (label, expiries) => {
  if (!expiries.length) return null

  const callOi = expiries.reduce((sum, e) => sum + (e.callOpenInterest ?? 0), 0)
  const putOi = expiries.reduce((sum, e) => sum + (e.putOpenInterest ?? 0), 0)
  const heaviest = expiries.reduce(
    (top, e) => (e.callOpenInterest + e.putOpenInterest > top.callOpenInterest + top.putOpenInterest ? e : top),
    expiries[0],
  )

  return {
    label,
    ratio: callOi > 0 ? putOi / callOi : 0,
    openInterest: callOi + putOi,
    impliedVolatility: mean(expiries.map((e) => e.atmImpliedVolatility ?? 0)),
    magnetStrike: heaviest.topCallStrike,
    span: `${String(expiries[0].date).slice(0, 7)} → ${String(expiries[expiries.length - 1].date).slice(0, 7)}`,
  }
}

export default function OptionsMarketPanel({ options }) {
  const [detailsOpen, setDetailsOpen] = useState(false)
  if (!options?.expiries?.length) return null

  const nearTerm = options.expiries.filter((e) => !e.isLongTerm)
  const leaps = options.expiries.filter((e) => e.isLongTerm)
  const horizons = [summarizeHorizon('Near-term', nearTerm), summarizeHorizon('LEAPs · long-dated', leaps)].filter(Boolean)

  const tilt = tiltLabel(options.putCallOpenInterestRatio)
  const strikes = options.topStrikes ?? []
  const maxStrikeOi = Math.max(1, ...strikes.map((s) => Math.max(s.callOpenInterest, s.putOpenInterest)))
  const maxExpiryOi = Math.max(1, ...options.expiries.map((e) => e.callOpenInterest + e.putOpenInterest))
  const painGap = options.price > 0 ? (options.maxPainStrike - options.price) / options.price : 0

  return (
    <div className="onds-card p-4 md:p-5">
      <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
        <div className="flex items-center gap-2">
          <Target className="h-5 w-5" style={{ color: 'var(--onds-accent)' }} />
          <h2 className="text-base md:text-lg font-bold">Options Market</h2>
        </div>
        <span className="onds-chip text-[11px]" style={{ color: toneColor(tilt.tone), borderColor: `${toneColor(tilt.tone)}55` }}>
          {tilt.text}
        </span>
      </div>

      <p className="text-xs text-[var(--onds-text-dim)] mb-4">
        Max pain{' '}
        <span className="font-semibold" style={{ color: 'var(--onds-accent)' }}>{formatPrice(options.maxPainStrike)}</span>
        {' '}sits{' '}
        <span className="font-semibold" style={{ color: toneColor(painGap >= 0 ? 'bull' : 'bear') }}>
          {formatSignedPercent(painGap)}
        </span>
        {' '}vs spot {formatPrice(options.price)}, with ATM implied vol at {formatPercent(options.atmImpliedVolatility, 0)}
        {options.unusual?.length > 0 && ` and ${options.unusual.length} strikes trading above their open interest`}.
      </p>

      <div className="grid sm:grid-cols-2 gap-3">
        {horizons.map((horizon) => (
          <HorizonCard key={horizon.label} horizon={horizon} />
        ))}
      </div>

      <button
        onClick={() => setDetailsOpen((open) => !open)}
        className="flex items-center gap-1.5 mt-4 text-xs font-semibold"
        style={{ color: 'var(--onds-text-dim)' }}
      >
        {detailsOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        {detailsOpen ? 'Hide' : 'Show'} full strike ladder, expiries &amp; unusual flow
      </button>

      {detailsOpen && (
        <div className="mt-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <Stat label="Put/Call OI" value={(options.putCallOpenInterestRatio ?? 0).toFixed(2)} tone={tilt.tone} />
            <Stat label="Put/Call volume" value={(options.putCallVolumeRatio ?? 0).toFixed(2)} />
            <Stat label="Calls OI" value={formatCompact(options.callOpenInterest)} tone="bull" />
            <Stat label="Puts OI" value={formatCompact(options.putOpenInterest)} tone="bear" />
          </div>

          <h3 className="text-xs font-bold mb-2">Where · open interest by strike</h3>
          <div className="flex flex-col gap-1 mb-1">
            {strikes.map((s) => (
              <StrikeRow key={s.strike} strike={s} maxOi={maxStrikeOi} spot={options.price} />
            ))}
          </div>

          <ExpiryLadder title="When · near-term" expiries={nearTerm} maxOi={maxExpiryOi} />
          <ExpiryLadder title="When · LEAPs (long-dated)" expiries={leaps} maxOi={maxExpiryOi} leap />

          {options.unusual?.length > 0 && <UnusualFlow flows={options.unusual} />}
        </div>
      )}
    </div>
  )
}

function HorizonCard({ horizon }) {
  const tilt = tiltLabel(horizon.ratio)

  return (
    <div className="rounded-lg p-3 border" style={{ backgroundColor: 'var(--onds-surface)', borderColor: 'var(--onds-border)' }}>
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <p className="text-[11px] font-bold leading-tight">{horizon.label}</p>
        <span className="text-[10px] text-[var(--onds-text-dim)] tabular-nums">{horizon.span}</span>
      </div>

      <div className="flex items-baseline justify-between mb-0.5">
        <span className="text-[10px] text-[var(--onds-text-dim)]">Put/call OI</span>
        <span className="text-sm font-bold tabular-nums" style={{ color: toneColor(tilt.tone) }}>
          {horizon.ratio.toFixed(2)}
        </span>
      </div>
      <div className="flex items-baseline justify-between mb-0.5">
        <span className="text-[10px] text-[var(--onds-text-dim)]">Heaviest call strike</span>
        <span className="text-xs font-semibold tabular-nums">{formatPrice(horizon.magnetStrike)}</span>
      </div>
      <div className="flex items-baseline justify-between">
        <span className="text-[10px] text-[var(--onds-text-dim)]">Open interest · ATM IV</span>
        <span className="text-xs font-semibold tabular-nums">
          {formatCompact(horizon.openInterest)} · {formatPercent(horizon.impliedVolatility, 0)}
        </span>
      </div>
    </div>
  )
}

function StrikeRow({ strike, maxOi, spot }) {
  const atSpot = Math.abs(strike.strike - spot) < 0.26

  return (
    <div className="flex items-center gap-2 text-[11px]">
      <span className="w-14 text-right tabular-nums font-semibold shrink-0" style={atSpot ? { color: 'var(--onds-accent)' } : undefined}>
        {formatPrice(strike.strike)}
      </span>
      <div className="flex-1 flex items-center gap-1 min-w-0">
        <div className="flex-1 flex justify-end">
          <div className="h-3 rounded-l" style={{ width: `${pct(strike.putOpenInterest, maxOi)}%`, backgroundColor: BEAR, opacity: 0.75 }} />
        </div>
        <div className="flex-1">
          <div className="h-3 rounded-r" style={{ width: `${pct(strike.callOpenInterest, maxOi)}%`, backgroundColor: BULL, opacity: 0.75 }} />
        </div>
      </div>
      <span className="w-24 text-[10px] text-[var(--onds-text-dim)] tabular-nums shrink-0">
        {formatCompact(strike.putOpenInterest)}p · {formatCompact(strike.callOpenInterest)}c
      </span>
    </div>
  )
}

function ExpiryLadder({ title, expiries, maxOi, leap }) {
  if (!expiries?.length) return null

  return (
    <>
      <h3 className="text-xs font-bold mb-2 mt-4 flex items-center gap-2">
        {title}
        {leap && (
          <span className="onds-chip text-[9px] font-normal" style={{ color: 'var(--onds-accent)', borderColor: 'var(--onds-accent)55' }}>
            multi-year
          </span>
        )}
      </h3>
      <div className="flex flex-col gap-1.5">
        {expiries.map((e) => (
          <div key={e.date} className="flex items-center gap-2 text-[11px]">
            <span className="w-20 tabular-nums shrink-0">{String(e.date).slice(0, 10)}</span>
            <span className="w-10 text-[10px] text-[var(--onds-text-dim)] tabular-nums shrink-0">{e.daysToExpiry}d</span>
            <div className="flex-1 flex h-3 rounded overflow-hidden min-w-0" style={{ backgroundColor: 'var(--onds-border)' }}>
              <div style={{ width: `${pct(e.callOpenInterest, maxOi)}%`, backgroundColor: BULL, opacity: 0.8 }} />
              <div style={{ width: `${pct(e.putOpenInterest, maxOi)}%`, backgroundColor: BEAR, opacity: 0.8 }} />
            </div>
            <span className="w-32 text-[10px] text-[var(--onds-text-dim)] tabular-nums shrink-0 text-right">
              pain {formatPrice(e.maxPainStrike)} · IV {formatPercent(e.atmImpliedVolatility, 0)}
            </span>
          </div>
        ))}
      </div>
    </>
  )
}

function UnusualFlow({ flows }) {
  return (
    <>
      <div className="flex items-center gap-1.5 mt-4 mb-2">
        <Zap className="h-3.5 w-3.5" style={{ color: 'var(--onds-caution)' }} />
        <h3 className="text-xs font-bold">Fresh positioning · volume above open interest</h3>
      </div>
      <div className="flex flex-wrap gap-2">
        {flows.slice(0, 6).map((u, i) => {
          const color = u.type === 'CALL' ? BULL : BEAR
          return (
            <div
              key={`${u.type}${u.strike}${u.expiry}${i}`}
              className="rounded-lg px-2.5 py-1.5 border text-[10px]"
              style={{ borderColor: `${color}55`, backgroundColor: 'var(--onds-surface)' }}
            >
              <span className="font-bold" style={{ color }}>{u.type} {formatPrice(u.strike)}</span>
              <span className="text-[var(--onds-text-dim)]"> · {String(u.expiry).slice(5, 10)} · </span>
              <span className="font-semibold tabular-nums">{formatCompact(u.volume)} vol</span>
              <span className="text-[var(--onds-text-dim)] tabular-nums"> ({u.volumeToOpenInterestRatio.toFixed(1)}x OI)</span>
            </div>
          )
        })}
      </div>
    </>
  )
}

function Stat({ label, value, tone }) {
  return (
    <div className="rounded-lg p-2.5 border" style={{ backgroundColor: 'var(--onds-surface)', borderColor: 'var(--onds-border)' }}>
      <p className="text-[10px] text-[var(--onds-text-dim)] mb-0.5">{label}</p>
      <p className="text-sm font-bold tabular-nums" style={tone ? { color: toneColor(tone) } : undefined}>{value}</p>
    </div>
  )
}
